import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type { UpdatePaymentInput, UpdatePaymentResponse } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

/**
 * Hook for updating payment fields.
 * Handles the API call, success/error states, and cache invalidation.
 */
export function useUpdatePayment() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<UpdatePaymentResponse, Error, UpdatePaymentInput>({
    mutationFn: async (input) => {
      return await rpcClient.finance.payments.update(input);
    },
    onSuccess: (data, variables) => {
      if (data.payment) {
        const combinedCount = data.combinedPaymentIds?.length ?? 0;
        showToast({
          title: 'Success',
          message:
            combinedCount > 0
              ? `Payment updated and combined with ${combinedCount} other payment${combinedCount > 1 ? 's' : ''} on the same date`
              : 'Payment updated successfully',
          color: 'success',
          icon: 'check',
        });

        // Invalidate payment queries to refresh the data
        void queryClient.invalidateQueries({
          queryKey: orpc.finance.payments.get.queryKey({
            input: { id: variables.id },
          }),
        });
        void queryClient.invalidateQueries({
          queryKey: orpc.finance.payments.list.queryKey({
            input: {},
          }),
        });
      }
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to update payment',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
